import React, {useEffect, useMemo, useState} from 'react'
import {useNavigate, useSearchParams} from 'react-router-dom'
import {publicSubmitApplication, publicUploadResume, getPublicJobPostById} from '../../../api/HiringAPI'

type Step = 'PERSONAL' | 'EXPERIENCE' | 'RESUME'

const steps: Step[] = ['PERSONAL', 'EXPERIENCE', 'RESUME']

interface Props { jobPostId?: string }
const PublicApplicationForm: React.FC<Props> = ({ jobPostId: jobPostIdProp }) => {
  const [params] = useSearchParams()
  const jobPostId = jobPostIdProp || params.get('jobPostId') || ''
  const navigate = useNavigate()

  const [stepIdx, setStepIdx] = useState<number>(0)
  const step = useMemo(() => steps[stepIdx], [stepIdx])

  const [form, setForm] = useState<any>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    currentLocation: '',
    currentCompany: '',
    currentRole: '',
    experience: '',
    resumeUrl: '',
  })
  const [uploading, setUploading] = useState<boolean>(false)
  const [submitting, setSubmitting] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const [success, setSuccess] = useState<string>('')
  const [alreadyApplied, setAlreadyApplied] = useState<boolean>(false)
  const [jd, setJd] = useState<any>(null)

  useEffect(() => {
    let mounted = true
    ;(async () => {
      if (!jobPostId) return
      const res = await getPublicJobPostById(jobPostId)
      const record = res?.data || res
      if (!mounted) return
      setJd(record)
    })()
    return () => { mounted = false }
  }, [jobPostId])

  const canNext = useMemo(() => {
    if (step === 'PERSONAL') return Boolean(form.firstName && form.lastName && form.email)
    if (step === 'EXPERIENCE') return true
    if (step === 'RESUME') return Boolean(form.resumeUrl)
    return false
  }, [step, form])

  const handleUpload = async (file: File) => {
    setError('')
    setSuccess('')
    setUploading(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('jobPostId', jobPostId)
      fd.append('candidateName', `${form.firstName} ${form.lastName}`.trim())
      const res = await publicUploadResume(fd)
      if (res?.success) {
        setForm((f: any) => ({ ...f, resumeUrl: res.data.url }))
      } else {
        setError(res?.message || 'Upload failed')
      }
    } catch (e: any) {
      setError(e?.message || 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  const handleSubmit = async () => {
    setError('')
    setSuccess('')
    setSubmitting(true)
    try {
      const payload = {
        jobPostId,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        currentLocation: form.currentLocation,
        currentCompany: form.currentCompany,
        currentRole: form.currentRole,
        experience: form.experience ? Number(form.experience) : undefined,
        resumeUrl: form.resumeUrl,
      }
      const res = await publicSubmitApplication(payload)
      if (res?.success) {
        setSuccess('Application submitted successfully')
        navigate('/application-thank-you', { replace: true })
      } else {
        const msg = String(res?.message || 'Submission failed')
        const lower = msg.toLowerCase()
        if (lower.includes('already applied')) {
          setAlreadyApplied(true)
          setError('You have already applied to this job with this email. No further action is required.')
        } else if (lower.includes('job post not found')) {
          setError('This job is no longer accepting applications.')
        } else {
          setError(msg)
        }
      }
    } catch (e: any) {
      setError(e?.message || 'Submission failed')
    } finally {
      setSubmitting(false)
    }
  }

  if (!jobPostId) {
    return <div className='container py-10'><div className='alert alert-danger'>Missing jobPostId</div></div>
  }

  return (
    <div className='container py-10' style={{maxWidth: 720}}>
      <div className='card'>
        <div className='card-header d-flex align-items-center justify-content-between'>
          <h3 className='card-title fw-bold mb-0'>Candidate Application Form</h3>
          <div className='text-muted'>{stepIdx + 1} / {steps.length}</div>
        </div>
        <div className='card-body'>
          {error && (
            <div className={`alert ${alreadyApplied ? 'alert-warning' : 'alert-danger'}`}>{error}</div>
          )}
          {success && <div className='alert alert-success'>{success}</div>}

          {/* Step tabs */}
          <div className='d-flex gap-3 mb-6'>
            <div className={`badge ${step === 'PERSONAL' ? 'badge-light-primary' : 'badge-light'}`}>Personal Info</div>
            <div className={`badge ${step === 'EXPERIENCE' ? 'badge-light-primary' : 'badge-light'}`}>Experience</div>
            <div className={`badge ${step === 'RESUME' ? 'badge-light-primary' : 'badge-light'}`}>Resume Upload</div>
          </div>

          {step === 'PERSONAL' && (
            <div className='row g-5'>
              <div className='col-md-6'>
                <label className='form-label'>First Name</label>
                <input className='form-control' value={form.firstName} onChange={(e) => setForm({...form, firstName: e.target.value})} />
              </div>
              <div className='col-md-6'>
                <label className='form-label'>Last Name</label>
                <input className='form-control' value={form.lastName} onChange={(e) => setForm({...form, lastName: e.target.value})} />
              </div>
              <div className='col-md-6'>
                <label className='form-label'>Email</label>
                <input type='email' className='form-control' value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} />
              </div>
              <div className='col-md-6'>
                <label className='form-label'>Phone</label>
                <input className='form-control' value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} />
              </div>
              <div className='col-md-12'>
                <label className='form-label'>Current Location</label>
                <input className='form-control' value={form.currentLocation} onChange={(e) => setForm({...form, currentLocation: e.target.value})} />
              </div>
            </div>
          )}

          {step === 'EXPERIENCE' && (
            <div className='row g-5'>
              <div className='col-md-6'>
                <label className='form-label'>Company Name</label>
                <input className='form-control' value={form.currentCompany} onChange={(e) => setForm({...form, currentCompany: e.target.value})} />
              </div>
              <div className='col-md-6'>
                <label className='form-label'>Job Title</label>
                <input className='form-control' value={form.currentRole} onChange={(e) => setForm({...form, currentRole: e.target.value})} />
              </div>
              <div className='col-md-6'>
                <label className='form-label'>Experience (years)</label>
                <input type='number' className='form-control' value={form.experience} onChange={(e) => setForm({...form, experience: e.target.value})} />
              </div>
            </div>
          )}

          {step === 'RESUME' && (
            <div className='row g-5'>
              <div className='col-md-12'>
                <label className='form-label'>Resume (PDF/DOC/DOCX)</label>
                <input type='file' className='form-control' accept='.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document' onChange={(e) => {
                  const f = e.target.files?.[0]
                  if (f) handleUpload(f)
                }} />
                {uploading && <div className='text-muted mt-2'>Uploading...</div>}
                {form.resumeUrl && (
                  <div className='alert alert-info mt-3'>File uploaded successfully.</div>
                )}
              </div>
            </div>
          )}
        </div>
        <div className='card-footer d-flex justify-content-between'>
          <button className='btn btn-light' disabled={stepIdx === 0} onClick={() => setStepIdx((i) => Math.max(0, i - 1))}>Back</button>
          {stepIdx < steps.length - 1 ? (
            <button className='btn btn-primary' disabled={!canNext} onClick={() => setStepIdx((i) => Math.min(steps.length - 1, i + 1))}>Next</button>
          ) : (
            <div className='d-flex gap-3'>
              {alreadyApplied && (
                <button className='btn btn-light' onClick={() => navigate(`/jd/${jobPostId}`)}>Back to JD</button>
              )}
              <button className='btn btn-primary' disabled={!canNext || submitting || alreadyApplied} onClick={handleSubmit}>
                {alreadyApplied ? 'Already Applied' : 'Submit Application'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default PublicApplicationForm


